/**
 * Plan 工作台路由外壳（V3 PR0 新写，判定卡 01）：一个路由 ＋ 页内 hash 页签，按 hash 分发各页签（清单见 plan-tabs.ts）。
 * PR6：提示词与模型页头放「保存为新快照」；查询参数 `snapshot` 给配置版本页开对比抽屉。
 * PR9：模拟场景上线；查询参数 `batch`（模拟场景报告的批次）刷新与分享链接能还原（放查询参数而不放 hash，侧栏选中态按 hash 判）。
 * MVP-0 的 8 项接真实页面（刀 0 原样拷来的 5 页在后端接口上线前显示空态 ／ 失败态；模式对比、配置版本先出页头 ＋ 空态；
 * 链路总览是静态示意），后续期 8 项是占位页。plan 页面只调 /admin/v1/plan-lab/*（D-20 完全隔离）。
 */
import { createFileRoute, useLocation } from "@tanstack/react-router";

import { PlaygroundTab } from "@/components/plan-lab/ask/playground-tab";
import { BizParamsTab } from "@/components/plan-lab/biz-params/biz-params-tab";
import { BoardTab } from "@/components/plan-lab/board/board-tab";
import { ConfigVersionsTab } from "@/components/plan-lab/config-versions/config-versions-tab";
import { EvalBankTab } from "@/components/plan-lab/eval-config/bank-tab";
import { EvalCxTab } from "@/components/plan-lab/eval-config/cx-tab";
import { EvalJudgesTab } from "@/components/plan-lab/eval-config/judges-tab";
import { EvalRulesTab } from "@/components/plan-lab/eval-config/scoring-rules-tab";
import { EvalRecordsTab } from "@/components/plan-lab/eval-records/eval-records-tab";
import { EvalReportsTab } from "@/components/plan-lab/eval-reports/eval-reports-tab";
import { EvalTab } from "@/components/plan-lab/eval/eval-tab";
import { FieldContractTab } from "@/components/plan-lab/field-contract/field-contract-tab";
import { GuidanceTab } from "@/components/plan-lab/guidance/guidance-tab";
import { InfoFlowTab } from "@/components/plan-lab/info-flow/info-flow-tab";
import { KnowledgeTab } from "@/components/plan-lab/knowledge/knowledge-tab";
import { MethodSkillsTab } from "@/components/plan-lab/method-skills/method-skills-tab";
import {
	OverviewTab,
	parseOverviewStep,
	parseOverviewView,
} from "@/components/plan-lab/overview/overview-tab";
import { PromptsPanel, PromptsSaveButton } from "@/components/plan-lab/prompts/prompts-panel";
import { RecallEvalTab } from "@/components/plan-lab/recall/recall-eval-tab";
import { RecallTab } from "@/components/plan-lab/recall/recall-tab";
import { HistoryPanel } from "@/components/plan-lab/records/history-panel";
import { RulesTab } from "@/components/plan-lab/rules/rules-tab";
import { PlaceholderPage } from "@/components/plan-lab/shell/placeholder-page";
import { PLAN_TABS, parsePlanTab } from "@/components/plan-lab/shell/plan-tabs";
import { SignalTab } from "@/components/plan-lab/signal/signal-tab";
import { StatsTab } from "@/components/plan-lab/stats/stats-tab";
import { TagsTab } from "@/components/plan-lab/tags/tags-tab";
import { ToolsTab } from "@/components/plan-lab/tools/tools-tab";
import { PageHeader } from "@/components/shared/page-help";
import { usePlanMeta } from "@/lib/api/plan-lab";

/** 查询参数：`snapshot`（配置版本页打开该快照，O-8；PR6）；`batch`（模拟场景）；`rr` ／ `rrb`（召回评测）；`rule`（运行规则页打开该条，批 4 信号识别页跳过来；判分规则页同用，判官提示页跳过来）；`tag`（标签字典打开该标签）；`view`（链路总览的页签）；`step`（链路总览运行时流程高亮哪一步）；`stage`（运行规则页按环节筛选） */
export type PlanLabSearch = {
	snapshot?: string;
	batch?: string;
	/** 召回评测：看哪次回放 ／ 对比哪次（P1 召回第 5 步） */
	rr?: string;
	rrb?: string;
	/** 运行规则：打开哪条规则（其他页的规则编号跳过来） */
	rule?: string;
	/** 标签字典：打开哪个标签（标签编号；信号识别页的意图跳过来） */
	tag?: string;
	/** 链路总览：哪个页签（runtime 运行时流程；不带或其他值＝页面地图，旧的 arch 也落到页面地图） */
	view?: string;
	/** 运行规则：按哪个环节筛选（链路总览每步的「规则 N 条」跳过来，取值是规则的环节名） */
	stage?: string;
	/** 链路总览运行时流程：高亮哪一步（页面地图详情区点 ①～⑦ 带过来） */
	step?: string;
};
const SEARCH_KEYS = [
	"snapshot",
	"batch",
	"rr",
	"rrb",
	"rule",
	"tag",
	"view",
	"step",
	"stage",
] as const;

export const Route = createFileRoute("/plan-lab")({
	validateSearch: (s: Record<string, unknown>): PlanLabSearch => {
		const out: PlanLabSearch = {};
		for (const k of SEARCH_KEYS) {
			const v = s[k];
			if (typeof v === "string" && v) out[k] = v;
			// 「#253」这类 Loop 批次号被路由按 JSON 解析后可能变成数字，统一回字符串
			else if (typeof v === "number") out[k] = String(v);
		}
		return out;
	},
	component: PlanLabPage,
});

function PlanLabPage() {
	// LLM key 是否配置读元信息 A-01（PR3；原先读 Loop 的 /testset，plan 没有该接口，曾误显示「需配置 LLM key」）。
	// 拿不到时不拦（后端会回 llm_key_missing）
	const planMeta = usePlanMeta();
	const hasKey = planMeta.data?.llm_key_configured !== false;
	const location = useLocation();
	const search = Route.useSearch();
	const tab = parsePlanTab(location.hash || "");
	const meta = PLAN_TABS[tab];

	return (
		<div className="space-y-4" data-testid="plan-page" data-plan-tab={tab}>
			<PageHeader moduleId="V3-plan-lab" title={meta.title} description={meta.desc}>
				{/* 提示词与模型：页头唯一实色「保存为新快照」（PR6，线框 04 §五） */}
				{tab === "prompts" ? <PromptsSaveButton /> : null}
			</PageHeader>

			{meta.placeholder ? <PlaceholderPage phase={meta.placeholder} /> : null}
			{tab === "overview" ? (
				<OverviewTab view={parseOverviewView(search.view)} step={parseOverviewStep(search.step)} />
			) : null}
			{tab === "playground" ? <PlaygroundTab hasKey={hasKey} /> : null}
			{tab === "eval" ? <EvalTab hasKey={hasKey} batchId={search.batch} /> : null}
			{tab === "history" ? <HistoryPanel /> : null}
			{tab === "eval-records" ? <EvalRecordsTab /> : null}
			{tab === "eval-reports" ? <EvalReportsTab /> : null}
			{tab === "stats" ? <StatsTab /> : null}
			{tab === "prompts" ? <PromptsPanel /> : null}
			{tab === "config-versions" ? <ConfigVersionsTab /> : null}
			{tab === "method-skills" ? <MethodSkillsTab /> : null}
			{tab === "recall" ? <RecallTab /> : null}
			{tab === "tools" ? <ToolsTab /> : null}
			{tab === "guards" ? <RulesTab focusId={search.rule} stageFilter={search.stage} /> : null}
			{tab === "tags" ? <TagsTab focusCode={search.tag} /> : null}
			{tab === "signal" ? <SignalTab /> : null}
			{tab === "planner" ? <BoardTab /> : null}
			{tab === "guidance" ? <GuidanceTab /> : null}
			{tab === "business-params" ? <BizParamsTab /> : null}
			{tab === "knowledge" ? <KnowledgeTab /> : null}
			{tab === "field-contract" ? <FieldContractTab /> : null}
			{tab === "info-flow" ? <InfoFlowTab /> : null}
			{tab === "eval-bank" ? <EvalBankTab /> : null}
			{tab === "eval-cx" ? <EvalCxTab /> : null}
			{tab === "eval-judges" ? <EvalJudgesTab /> : null}
			{tab === "eval-rules" ? <EvalRulesTab focusId={search.rule} /> : null}
			{tab === "recall-eval" ? <RecallEvalTab runId={search.rr} compareId={search.rrb} /> : null}
		</div>
	);
}
